import React, { useState, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { close } from 'ionicons/icons';

interface TransferLetterModalProps {
  isOpen: boolean;
  memberName: string;
  onDismiss: () => void;
  onConfirm: (data: {
    destination_church: string;
    destination_city?: string;
    decision_date?: string;
    special_mentions?: string;
    membership_period?: string;
  }) => void;
  isLoading?: boolean;
  defaultChurch?: string;
  defaultCity?: string;
  waterBaptismDate?: string | null;
}

const TransferLetterModal: React.FC<TransferLetterModalProps> = ({
  isOpen,
  memberName,
  onDismiss,
  onConfirm,
  isLoading = false,
  defaultChurch = '',
  defaultCity = '',
  waterBaptismDate,
}) => {
  const [destinationChurch, setDestinationChurch] = useState(defaultChurch);
  const [destinationCity, setDestinationCity] = useState(defaultCity);
  const [decisionDate, setDecisionDate] = useState(
    new Date().toISOString().split('T')[0]!,
  );
  const [specialMentions, setSpecialMentions] = useState('');
  const [membershipPeriod, setMembershipPeriod] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDestinationChurch(defaultChurch);
      setDestinationCity(defaultCity);
      if (waterBaptismDate) {
        const d = new Date(waterBaptismDate);
        const formatted = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
        const now = new Date();
        const nowFormatted = `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}`;
        setMembershipPeriod(`${formatted} – ${nowFormatted}`);
      } else {
        setMembershipPeriod('');
      }
    }
  }, [isOpen, defaultChurch, defaultCity, waterBaptismDate]);

  const handleConfirm = () => {
    if (!destinationChurch.trim()) return;
    onConfirm({
      destination_church: destinationChurch.trim(),
      destination_city: destinationCity.trim() || undefined,
      decision_date: decisionDate || undefined,
      special_mentions: specialMentions.trim() || undefined,
      membership_period: membershipPeriod.trim() || undefined,
    });
  };

  const handleDismiss = () => {
    setSpecialMentions('');
    setMembershipPeriod('');
    onDismiss();
  };

  if (!isOpen) return null;

  return (
    <div className="crm-popup-overlay" onClick={handleDismiss}>
      <div className="crm-popup" onClick={(e) => e.stopPropagation()}>
        <div className="crm-popup__header">
          <h3 className="crm-popup__title">Adeverință Transfer</h3>
          <button className="crm-popup__close" onClick={handleDismiss}>
            <IonIcon icon={close} />
          </button>
        </div>

        <div className="crm-popup__body">
          <p className="crm-popup__hint">
            Generează adeverință de transfer pentru <strong>{memberName}</strong>.
          </p>

          <div className="crm-popup__field">
            <label className="crm-popup__label">Biserica Destinație *</label>
            <input
              className="crm-popup__input"
              value={destinationChurch}
              onChange={(e) => setDestinationChurch(e.target.value)}
              placeholder="Numele bisericii destinație"
            />
          </div>

          <div className="crm-popup__row">
            <div className="crm-popup__field">
              <label className="crm-popup__label">Localitatea</label>
              <input
                className="crm-popup__input"
                value={destinationCity}
                onChange={(e) => setDestinationCity(e.target.value)}
                placeholder="Orașul / localitatea"
              />
            </div>
            <div className="crm-popup__field">
              <label className="crm-popup__label">Data Deciziei</label>
              <input
                className="crm-popup__input"
                type="date"
                value={decisionDate}
                onChange={(e) => setDecisionDate(e.target.value)}
              />
            </div>
          </div>

          <div className="crm-popup__field">
            <label className="crm-popup__label">Perioada de Membru</label>
            <input
              className="crm-popup__input"
              value={membershipPeriod}
              onChange={(e) => setMembershipPeriod(e.target.value)}
              placeholder="ex: 2015 – 2026 (opțional, se completează automat)"
            />
          </div>

          <div className="crm-popup__field">
            <label className="crm-popup__label">Mențiuni Speciale</label>
            <textarea
              className="crm-popup__textarea"
              value={specialMentions}
              onChange={(e) => setSpecialMentions(e.target.value)}
              placeholder="Mențiuni speciale (opțional)..."
              rows={2}
            />
          </div>
        </div>

        <div className="crm-popup__footer">
          <button className="crm-popup__btn crm-popup__btn--secondary" onClick={handleDismiss}>
            Anulează
          </button>
          <button
            className="crm-popup__btn crm-popup__btn--primary"
            onClick={handleConfirm}
            disabled={!destinationChurch.trim() || isLoading}
          >
            {isLoading ? 'Se generează...' : 'Generează'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferLetterModal;
